import React from 'react';
import { useDeleteTopicModal } from '../hooks/useDeleteTopicModal';
import {
  SlDialog,
  SlDialogRoot,
  SlDialogTitle,
  SlDialogCloseButton,
  SlDialogContent
} from 'src/components/BeaconV2Components/SlDialog';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { topicSettingsServerProxy } from '../serverProxy';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import { useHistory, useSnackbar } from 'src/utils/Hooks';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';
import { useTopicPolling } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useTopicPolling';

/**
 * Modal used for deleting owned topics
 * Controlled via the useDeleteTopicModal hook
 */
export const DeleteTopicModal = () => {
  const { isOpen, closeModal, topicId } = useDeleteTopicModal();
  const { showSnackbar } = useSnackbar();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { pollTopicUpdate } = useTopicPolling();

  const { mutateAsync: deleteTopic, isLoading } = useMutation({
    mutationFn: async () => {
      await topicSettingsServerProxy.deleteTopic(topicId);
      await topicSettingsServerProxy.invalidateCache();
    },
    onSuccess: () => {
      queryClient.invalidateQueries([TOPIC_QUERY_KEYS.all]);
      pollTopicUpdate(topicId, `deleting-${topicId}`);
    }
  });

  const handleDeleteTopic = async () => {
    if (isLoading) {
      return;
    }

    try {
      await deleteTopic();

      const params = new URLSearchParams(history.location.search);
      params.set('tab', 'summary');
      params.set('entityType', 'searchtermlist');
      params.set('subtab', 'beacon');

      history.push(`/home?${params.toString()}`);
      showSnackbar({
        type: 'success',
        message: "You've successfully deleted the topic."
      });
    } catch (error) {
      showSnackbar({
        type: 'error',
        message: <GenericErrorMessage />
      });
    } finally {
      closeModal();
    }
  };

  return (
    <SlDialogRoot>
      <SlDialog open={isOpen} onClose={() => closeModal()} width="500px" height="180px">
        <SlDialogCloseButton
          onClick={() => closeModal()}
          style={{
            height: '16px',
            width: '16px'
          }}
        />
        <SlDialogContent
          sx={{
            padding: '24px 8px 0px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}
        >
          <SlDialogTitle>Delete Topic</SlDialogTitle>
          <Text>Are you sure you want to delete this topic? Once this topic is deleted, it cannot be recovered.</Text>
          <Flex justifyContent="flex-end" alignItems="center" gap="md">
            <SlButton onClick={() => closeModal()}>Cancel</SlButton>
            <SlButton variant="contained" onClick={() => handleDeleteTopic()}>
              {isLoading ? 'Deleting...' : 'Delete'}
            </SlButton>
          </Flex>
        </SlDialogContent>
      </SlDialog>
    </SlDialogRoot>
  );
};
